import React from "react";
import { FormField } from "../../../../shared/components/forms/FormField";
import { Card } from "../../../../shared/components/ui/Card";
import { InformationalNote } from "../../../../shared/components/ui/InformationalNote";
import type { FieldErrors, Control, UseFormSetValue } from "react-hook-form";
import type { AdverseEventType } from "../../../../api/client/types/catalog.types";
import type { AdverseEventNewFormData } from "../RegisterNewForm/RegisterNewForm.types";
import type { Swimmer } from "../../../../api/client/types/swimmer.types";
import { useAdverseEventNewFormFields } from "./useAdverseEventNewFormFields";
import { DisabledOptionsNote } from "../DisabledOptionsNote/DisabledOptionsNote";
import { getDisabledNote } from "../DisabledOptionsNote/getDisabledNote";
import { AdverseEventFactorsFormFields } from "../AdverseEventFactorsFormFields/AdverseEventFactorsFormFields";

interface AdverseEventNewFormFieldsProps {
  errors: FieldErrors<AdverseEventNewFormData>;
  control: Control<AdverseEventNewFormData>;
  setValue: UseFormSetValue<AdverseEventNewFormData>;
  adverseEventTypes: AdverseEventType[];
  swimmer?: Swimmer;
  showCard?: boolean;
  showTypeSelection?: boolean;
  isAccidentSelected?: boolean;
  isIncidentSelected?: boolean;
  isMeteorologicalConditionsSelected?: boolean;
  /** Numeric adverse event type ID — drives Q3 (nivel de intervención) fetch. */
  adverseEventsTypeId?: number | null;
}

/**
 * AdverseEventNewFormFields
 * Renders only the adverse event-specific fields.
 * Designed to be used within a parent form that handles common fields.
 *
 * Business rule: Only "Condiciones Meteorológicas" is available when there's no swimmer.
 * All other adverse event types require a swimmer to be associated.
 * 
 * @param showCard - If true, wraps content in a Card with title. Default: true
 * @param showTypeSelection - If true, shows adverse event type radio group. Default: true
 * @param isAccidentSelected - Override for accident selection state (for new UI pattern)
 * @param isMeteorologicalConditionsSelected - Override for meteorological selection state
 */
export const AdverseEventNewFormFields: React.FC<
  AdverseEventNewFormFieldsProps
> = ({
  errors,
  control,
  setValue,
  adverseEventTypes,
  swimmer,
  showCard = true,
  showTypeSelection = true,
  isAccidentSelected: isAccidentSelectedProp,
  isIncidentSelected: isIncidentSelectedProp,
  isMeteorologicalConditionsSelected: isMeteorologicalConditionsSelectedProp,
  adverseEventsTypeId,
}) => {
  // Use internal hooks only if type selection is shown (legacy behavior)
  const internalHookResult = useAdverseEventNewFormFields({
    control,
    adverseEventTypes,
    swimmer,
  });

  // Determine which values to use: props (new pattern) or hooks (legacy pattern)
  const isMeteorologicalConditionsSelected = 
    showTypeSelection 
      ? internalHookResult.isMeteorologicalConditionsSelected 
      : isMeteorologicalConditionsSelectedProp ?? false;
  
  const isAccidentSelected =
    showTypeSelection
      ? internalHookResult.isAccidentSelected
      : isAccidentSelectedProp ?? false;

  const isIncidentSelected =
    showTypeSelection
      ? internalHookResult.isIncidentSelected
      : isIncidentSelectedProp ?? false;

  const isAccidentOrIncidentSelected = isAccidentSelected || isIncidentSelected;

  const content = (
    <>
      {/* Adverse Event Type Selection - Only shown in legacy mode */}
      {showTypeSelection && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Adverse Event Type - Radio Buttons */}
          <div className="md:col-span-2">
            <FormField
              type="radioGroup"
              label="Tipo de Evento Adverso"
              required
              radioGroupProps={{
                control,
                name: "adverse_events_type_id",
                options: internalHookResult.adverseEventTypeOptions.map((opt) => ({
                  ...opt,
                  disabledNote: opt.disabled
                    ? getDisabledNote({
                        needsSwimmer: opt.needsSwimmer || false,
                        doesntNeedSwimmer: opt.doesntNeedSwimmer || false,
                        reportTypeName: "evento adverso",
                        gender: "masculine",
                      })
                    : undefined,
                })),
              }}
              error={errors.adverse_events_type_id?.message}
            />

            {/* Note about disabled options */}
            {internalHookResult.hasDisabledOptions && (
              <DisabledOptionsNote
                swimmer={swimmer}
                reportTypeName="evento adverso"
                gender="masculine"
                additionalInfo={
                  swimmer
                    ? '"Condiciones Meteorológicas" debe crearse sin bañista asociado.'
                    : 'Solo "Condiciones Meteorológicas" está disponible sin bañista.'
                }
              />
            )}
          </div>
        </div>
      )}

      {/* Q1, Q2, Q3 — only for Accidente and Incidentes */}
      {isAccidentOrIncidentSelected && (
        <AdverseEventFactorsFormFields
          control={control}
          setValue={setValue}
          errors={errors}
          adverseEventsTypeId={adverseEventsTypeId ?? null}
          isAccidentSelected={isAccidentSelected}
        />
      )}

      {/* Informational Note for Condiciones Meteorológicas */}
      {isMeteorologicalConditionsSelected && (
        <InformationalNote className={showTypeSelection ? "mt-5" : "mb-4"}>
          Previo a esta notificación, debe haber enviado un video de la
          Condición Meteorológica para que la Central de Comunicaciones
          autorice el diligenciamiento de este ítem
        </InformationalNote>
      )}
    </>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card variant="formSection">
      <h2 className="text-lg font-medium text-gray-800 mb-4">
        Información de Evento Adverso
      </h2>
      {content}
    </Card>
  );
};
